import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ButtonInPhieu from '../../../../../component/button-in-phieu';
import { ConfirmDialog } from '../../../../../component/ConfirmDialog';
import { CODE, KEY, ORDER_TYPE_ID, REQUEST_FROM, REQUEST_TYPE, RESPONSE_MESSAGE } from '../../../../../utils/Constant';
import { getErrorMessage } from '../../../../../utils/FormatUtils';
import WfButton from '../../../../../workflows/components/WfButton';
import { WfConstTrackerClinical } from '../../../../../workflows/Constant';
import { WORKFLOW_MEDICAL_ITEM_STATUS_CODE } from '../../../../constants/Thuoc';
import { PhanHeTiepDonContext } from '../../../../contexts/PhanHeTiepDonContext';
import ModalKeVatTu from '../../../../modals/modal-ke-vat-tu/ModalKeVatTu';
import { IInPhieuChiDinhThuocParams } from '../../../../models/ThuocModels';
import {
	deleteDonThuoc,
	huyGuiDonThuoc,
	printPhieuChiDinhThuoc,
	saveDonThuoc,
} from '../../../../services/ThuocService';
import { useTabVatTuContext } from '../../context';

type ActionType = 'edit' | 'delete' | 'cancel';

type DialogActionProps = {
	open: boolean;
	action: ActionType;
};

type Props = {
	getDanhSachDonVatTu: () => void;
	generalInfo?: any;
};

const ThaoTacPhieuVatTu = ({ getDanhSachDonVatTu, generalInfo = {} }: Props) => {
	const { donVatTuDetail, setDonVatTuSelectedIndex, actionsRef } = useTabVatTuContext();
	const { benhNhanInfo, statusChangedEventFunc } = useContext(PhanHeTiepDonContext);

	const [guiPhieu, setGuiPhieu] = useState(false);
	const [dialogAction, setDialogAction] = useState<DialogActionProps>({
		open: false,
		action: 'edit',
	});
	const [isUpdateWorkflow, setIsUpdateWorkflow] = useState(false);

	const patientData = benhNhanInfo?.thongTinKhamBenh || generalInfo;
	const { patient, visit, encounter, orderId } = patientData;
	const isXuatKho = [
		WORKFLOW_MEDICAL_ITEM_STATUS_CODE.CHO_XUAT_KHO,
		WORKFLOW_MEDICAL_ITEM_STATUS_CODE.DA_XUAT_KHO,
	].includes(donVatTuDetail?.statusCode);

	const onDeleteDonVatTu = async () => {
		try {
			const response = await deleteDonThuoc(donVatTuDetail.id);
			if (CODE.SUCCESS === response.code) {
				toast.success(RESPONSE_MESSAGE.DELETE.SUCCESS);
				setDialogAction({
					open: false,
					action: 'edit',
				});
				getDanhSachDonVatTu();
				setDonVatTuSelectedIndex(0);
				statusChangedEventFunc?.();
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	useEffect(() => {
		setGuiPhieu(donVatTuDetail?.statusCode === WORKFLOW_MEDICAL_ITEM_STATUS_CODE.GUI_DON);
	}, [donVatTuDetail?.statusCode]);

	const WorkFlowButton = () => {
		return (
			<WfButton
				tracker={WfConstTrackerClinical.MEDICAL_ITEMS}
				objectId={donVatTuDetail?.id}
				prefixFunction={(statusCode: string, wfCallbackFn: () => void, cancel?: () => void) => {
					if (WORKFLOW_MEDICAL_ITEM_STATUS_CODE.CHUA_GUI_DON === statusCode) {
						onGuiDonVatTu(wfCallbackFn, cancel);
						return;
					}

					onHuyGuiDonVatTu(wfCallbackFn, cancel);
				}}
				additionalFunc={(status: any) => {
					onUpdateWorkflowStatus(status);
				}}
				forceRender={isUpdateWorkflow}
			></WfButton>
		);
	};

	const onGuiDonVatTu = async (callbackFn: () => void, cancel?: () => void) => {
		try {
			const { code, data } = await saveDonThuoc({
				...donVatTuDetail,
				encounterId: encounter?.id,
				orderTypeId: parseInt(ORDER_TYPE_ID.VAT_TU),
				orderId,
				lstMedicalItemOrder: donVatTuDetail?.lstMedicalItemOrderRes,
				formCode: REQUEST_FROM.KHAM_BENH,
				medicalItemTypeCode: REQUEST_TYPE.VAT_TU,
				visitObjectTypeCode: visit?.visitObjectTypeCode
			});
			if (CODE.SUCCESS === code) {
				toast.success('Gửi đơn vật tư thành công');
				callbackFn();
			} else {
				toast.error(getErrorMessage({ code, data }));
				cancel?.();
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
			cancel?.();
		}
	};

	const onHuyGuiDonVatTu = async (callbackFn: () => void, cancel?: () => void) => {
		try {
			const { code, data } = await huyGuiDonThuoc(donVatTuDetail?.id);
			if (CODE.SUCCESS === code) {
				toast.success('Huỷ gửi đơn vật tư thành công');
				callbackFn();
			} else {
				toast.error(getErrorMessage({ code, data }));
				cancel?.();
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
			cancel?.();
		}
	};

	const onUpdateWorkflowStatus = (workflowStatus: string) => {
		setGuiPhieu(workflowStatus === WORKFLOW_MEDICAL_ITEM_STATUS_CODE.GUI_DON);
		actionsRef.current.getDetailDonVatTu(donVatTuDetail?.id);
	};

	const PrintButton = () => {
		return (
			<ButtonInPhieu<IInPhieuChiDinhThuocParams>
				fetchExport={printPhieuChiDinhThuoc}
				params={{
					fileType: 'pdf',
					patientId: patient?.id,
					medicalItemGroupId: donVatTuDetail?.id,
					visitId: visit?.id,
					type: parseInt(ORDER_TYPE_ID.VAT_TU),
				}}
				label="In đơn vật tư (F3)"
				keyBtn={KEY.F3}
			/>
		);
	};

	if (!donVatTuDetail?.id) return <div className="bg-white spaces min-h-50"></div>;

	if (isXuatKho) {
		return <div className="d-flex gap-3 bg-white p-2">{PrintButton()}</div>;
	}

	return (
		<div className="d-flex gap-2 justify-content-start p-2 bg-white">
			{guiPhieu ? (
				<div className="d-flex gap-2 justify-content-center">
					{PrintButton()}
					{donVatTuDetail?.isAllowModify && WorkFlowButton()}
				</div>
			) : (
				<>
					{donVatTuDetail?.isAllowModify &&
						![WORKFLOW_MEDICAL_ITEM_STATUS_CODE.DANG_TONG_HOP].includes(
							donVatTuDetail?.statusCode
						) && (
							<>
								<Button
									onClick={() => {
										setDialogAction({
											open: true,
											action: 'edit',
										});
									}}
								>
									Sửa phiếu
								</Button>
								{WorkFlowButton()}
								<Button
									className="btn-danger"
									onClick={() => {
										setDialogAction({
											open: true,
											action: 'delete',
										});
									}}
								>
									Xóa phiếu
								</Button>
							</>
						)}
				</>
			)}

			{dialogAction.open && dialogAction.action === 'edit' && (
				<ModalKeVatTu
					open={dialogAction.open}
					handleClose={() => {
						setDialogAction({
							open: false,
							action: 'edit',
						});
					}}
					onSave={() => {
						setIsUpdateWorkflow(!isUpdateWorkflow);
						actionsRef.current.getDetailDonVatTu(donVatTuDetail?.id);
					}}
					itemEdit={donVatTuDetail}
					generalInfo={patientData}
				/>
			)}

			{dialogAction.open && ['delete'].includes(dialogAction.action) && (
				<ConfirmDialog
					className="z-index-1060"
					show={dialogAction.open}
					title="Thông báo"
					message={`Bạn có chắc chắn muốn xóa đơn vật tư này không ?`}
					yes="Xác nhận"
					close="Đóng"
					onCloseClick={() => {
						setDialogAction({
							open: false,
							action: 'edit',
						});
					}}
					onYesClick={onDeleteDonVatTu}
				/>
			)}
		</div>
	);
};

export default ThaoTacPhieuVatTu;
